import { ref } from 'vue'
import { Preferences } from '@capacitor/preferences'
import { Capacitor } from '@capacitor/core'
import { useAnalytics } from '@/composables/useAnalytics'
import { useFeedbackStore } from '@/stores/feedback'
import { useAppContextStore } from '@/stores/appContext'
import { getAppVersion } from '@/utils/appVersion'
import { recordPaywallDismissReason } from '@/services/paywallDismissReason.service'
import { maybeOfferStartWithOne } from '@/composables/useStartWithOne'
import { i18n } from '@/i18n'

/**
 * Pourquoi le parent a refermé le paywall.
 *
 * L'entonnoir montrait la chute entre `paywall_viewed` et l'essai sans jamais
 * dire ce qui la cause : on avait un seul retour utilisateur, c'est-à-dire une
 * anecdote. Ces six raisons sont un signal agrégé, et elles ne se valent pas —
 * « le prix » se traite en pricing, « j'en discute avec mon/ma partenaire » se
 * traite en relance, « j'ai eu un bug » ne se traite pas du tout en marketing.
 */
export const PAYWALL_DISMISS_REASONS = [
  'price',
  'not_ready',
  'partner_decision',
  'technical_issue',
  'more_free_time',
  'other',
] as const

export type PaywallDismissReason = typeof PAYWALL_DISMISS_REASONS[number]

/** Clé Capacitor Preferences : `{ count, answered }` sérialisé. */
const STATE_KEY = 'paywall_reason_modal_state'

/**
 * Plafond de sollicitations. On redemande tant que le parent n'a pas choisi de
 * raison — « Passer » et le balayage ne ferment pas le sujet, ils ne disent
 * rien — mais on s'arrête au bout de trois. Au-delà on n'apprend plus rien de
 * quelqu'un qui a déjà refusé trois fois, on lui coûte juste un geste de plus
 * chaque fois qu'il bute sur une porte payante.
 */
const MAX_PROMPTS = 3

interface ReasonModalState {
  /** Nombre de fois où la question a été affichée. */
  count: number
  /** Une des six raisons a été choisie : le sujet est clos pour de bon. */
  answered: boolean
}

const EMPTY_STATE: ReasonModalState = { count: 0, answered: false }

// État partagé : la question est posée une fois pour toute l'app, montée une
// seule fois dans App.vue. Une douzaine d'écrans ouvrent le paywall — chacun
// embarquant sa propre feuille, on empilerait des overlays.
const isOpen = ref(false)
const followupOpen = ref(false)
const surface = ref('')
const paywallKind = ref<'native' | 'web'>('native')

// Hors `ref` : rien de tout ceci n'est affiché, et la réactivité ferait
// re-rendre la feuille pendant qu'elle se ferme.
let chosenReason: PaywallDismissReason | null = null
// `true` au repos : tant qu'aucune fermeture n'est en cours, il n'y a rien à
// logguer. C'est la garde anti-double-tir — `did-dismiss` est le seul point
// d'écriture, et il peut arriver deux fois (fermeture programmatique suivie du
// retour Android, par exemple).
let cancelLogged = true
// Même garde pour le micro-prompt technique, et pour la même raison.
let followupLogged = true
let followupChoice: 'contact_form' | null = null
// Ce qu'on fait APRÈS la fin d'animation d'un overlay. Ouvrir la feuille
// suivante avant que la précédente ait fini de partir laisse Ionic avec deux
// overlays qui se disputent le backdrop.
let pendingAct: (() => void) | null = null
/**
 * Une seule question par session, quel que soit le plafond restant. Sans ça, un
 * parent qui bute sur trois fonctionnalités fermées en cinq minutes brûle ses
 * trois cartouches dans la même humeur : on aurait trois mesures, mais une
 * seule information. En mémoire uniquement, donc remis à zéro au lancement.
 */
let askedThisSession = false

async function readState(): Promise<ReasonModalState> {
  try {
    const { value } = await Preferences.get({ key: STATE_KEY })
    if (!value) return { ...EMPTY_STATE }
    const parsed = JSON.parse(value) as Partial<ReasonModalState>
    return {
      count: typeof parsed.count === 'number' && parsed.count >= 0 ? parsed.count : 0,
      answered: parsed.answered === true,
    }
  } catch {
    // Stockage illisible ou JSON corrompu : on repart de zéro. Poser la
    // question une fois de trop coûte un geste, la perdre pour toujours coûte
    // la mesure entière.
    return { ...EMPTY_STATE }
  }
}

async function writeState(state: ReasonModalState): Promise<void> {
  try {
    await Preferences.set({ key: STATE_KEY, value: JSON.stringify(state) })
  } catch {
    /* pas de stockage : la question pourra revenir plus souvent que prévu */
  }
}

/**
 * Toutes les écritures d'état passent par cette file. `markPrompted` et
 * `markAnswered` sont deux lire-puis-écrire sur la même clé : lancés en
 * parallèle, le second relit l'état d'avant le premier et l'écrase. La réponse
 * du parent est justement écrite en tâche de fond, à la fermeture de la
 * feuille — c'est exactement celle qu'on perdrait.
 */
let stateWrite: Promise<void> = Promise.resolve()

function updateState(mutate: (state: ReasonModalState) => ReasonModalState): Promise<void> {
  stateWrite = stateWrite.then(async () => {
    await writeState(mutate(await readState()))
  })
  return stateWrite
}

/**
 * Qui n'est plus interrogé, et pourquoi.
 *
 *   - l'abonné payant : il a converti, sa raison de ne pas convertir n'existe
 *     plus. Le parent en essai ou dont l'essai est fini, lui, reste interrogé —
 *     c'est exactement la population qu'on cherche à comprendre ;
 *   - celui qui a choisi une raison : on sait, on arrête ;
 *   - celui qui a déjà été sollicité trois fois sans jamais répondre ;
 *   - celui à qui on a déjà posé la question dans cette session.
 */
export async function shouldShowReasonModal(): Promise<boolean> {
  if (askedThisSession) return false
  if (isPaidSubscriber()) return false
  // La réponse précédente peut être encore en vol : la lire avant qu'elle ait
  // atterri reposerait une question déjà close.
  await stateWrite
  const state = await readState()
  if (state.answered) return false
  return state.count < MAX_PROMPTS
}

/** Même lecture défensive que `isPaidSubscriber` : sans foyer connu, la
 *  réponse est quand même enregistrée, simplement sans lien. */
function currentHouseholdId(): string | null {
  try {
    return useAppContextStore().currentHouseholdId ?? null
  } catch {
    return null
  }
}

/** Lecture défensive : un store Pinia absent ne doit pas casser la fermeture
 *  du paywall, et « abonnement inconnu » vaut « pas abonné ». */
function isPaidSubscriber(): boolean {
  try {
    return useAppContextStore().isPaidPremium === true
  } catch {
    return false
  }
}

/** Sollicitation consommée. Comptée à l'AFFICHAGE : un parent qui balaie la
 *  feuille sans répondre a quand même été dérangé. */
function markPrompted(): Promise<void> {
  return updateState((state) => ({ ...state, count: state.count + 1 }))
}

/** Une raison a été choisie : plus jamais la peine de demander. */
function markAnswered(): Promise<void> {
  return updateState((state) => ({ ...state, answered: true }))
}

/**
 * Point d'entrée unique, appelé quand le paywall s'est refermé sans achat.
 *
 * Deux issues, et `paywall_dismiss_reason` part dans les deux : question posée (la
 * raison arrive à la fermeture de la feuille) ou question tue (on loggue tout
 * de suite, `shown_reason_modal: false`). Ce qui ne doit jamais arriver, c'est
 * une fermeture de paywall qui ne laisse aucune trace.
 */
export async function handlePaywallCancelled(
  from: string,
  kind: 'native' | 'web' = 'native',
): Promise<void> {
  const analytics = useAnalytics()

  // Foyer qui n'a encore rien validé, refermant le paywall du sélecteur : la
  // carte « Commence avec {prénom} » prend la place de la question
  // (HARMONIA-93). La fermeture laisse quand même sa trace, question tue, et
  // ne consomme ni le plafond ni la question de la session.
  if (await maybeOfferStartWithOne(from)) {
    void analytics.logPaywallDismissReason(from, kind, null, false)
    return
  }

  if (!(await shouldShowReasonModal())) {
    void analytics.logPaywallDismissReason(from, kind, null, false)
    return
  }

  askedThisSession = true
  await markPrompted()

  surface.value = from
  paywallKind.value = kind
  chosenReason = null
  cancelLogged = false
  isOpen.value = true
}

/**
 * Contexte technique joint au message du formulaire de contact. Le parent
 * décrit ce qu'il a vu, ces quatre lignes disent où et sur quoi — sans elles,
 * un « ça a planté » n'est pas actionnable.
 */
async function buildTechnicalContext(): Promise<string> {
  const platform = Capacitor.isNativePlatform() ? Capacitor.getPlatform() : 'web'
  return i18n.global.t('paywall.dismiss_reason.tech_followup.context', {
    surface: surface.value || 'unknown',
    paywallKind: paywallKind.value,
    appVersion: await getAppVersion(),
    platform,
  })
}

export function usePaywallDismissReason() {
  /** Une raison touchée : on retient, et on ferme. L'écriture se fait à la fin. */
  function choose(reason: PaywallDismissReason) {
    chosenReason = reason
    isOpen.value = false
  }

  /** « Passer », balayage vers le bas, tap sur le fond : même issue, raison nulle. */
  function skip() {
    chosenReason = null
    isOpen.value = false
  }

  /**
   * Fin d'animation de la feuille : SEUL point d'écriture de `paywall_dismiss_reason`
   * quand la question a été posée. Le compter au tap ferait rater le balayage,
   * le compter aux deux le compterait deux fois.
   */
  function onDismissed() {
    if (cancelLogged) return
    cancelLogged = true

    const reason = chosenReason
    void useAnalytics().logPaywallDismissReason(surface.value, paywallKind.value, reason, true)
    // En base aussi : Amplitude ne voit pas la PWA et ne relie pas la réponse
    // au foyer, or c'est par foyer qu'on ciblera une éventuelle offre prix.
    void recordPaywallDismissReason({
      surface: surface.value,
      paywallKind: paywallKind.value,
      reason,
      householdId: currentHouseholdId(),
    })

    // Seule une vraie raison clôt le sujet. « Passer » et le balayage laissent
    // le plafond faire son travail : on redemandera, au plus MAX_PROMPTS fois.
    if (reason !== null) void markAnswered()

    // Le micro-prompt attend que la feuille soit vraiment partie.
    if (reason === 'technical_issue') {
      followupChoice = null
      followupLogged = false
      followupOpen.value = true
    }
  }

  /** « Oui, je vous explique » : vers le formulaire de contact existant. */
  function acceptSupport() {
    followupChoice = 'contact_form'
    pendingAct = () => {
      void buildTechnicalContext().then((context) => {
        useFeedbackStore().open('paywall_technical_issue', { prefillMessage: context })
      })
    }
    followupOpen.value = false
  }

  /** « Non merci » : mesuré aussi. Un refus de raconter est une information. */
  function declineSupport() {
    followupChoice = null
    pendingAct = null
    followupOpen.value = false
  }

  /**
   * Même règle que pour la feuille des raisons : un seul point d'écriture, à la
   * fin d'animation. Ça couvre la troisième sortie que le brief ne nomme pas —
   * fond touché, retour Android — qui laissait sinon un `technical_issue` sans
   * `support_routed_from_paywall` du tout. Sollicité sans réponse, c'est un
   * refus : `route: null`, comme « Non merci ».
   */
  function onFollowupDismissed() {
    if (!followupLogged) {
      followupLogged = true
      void useAnalytics().logSupportRoutedFromPaywall(surface.value, followupChoice)
    }
    const act = pendingAct
    pendingAct = null
    act?.()
  }

  return {
    isOpen,
    followupOpen,
    surface,
    paywallKind,
    reasons: PAYWALL_DISMISS_REASONS,
    choose,
    skip,
    onDismissed,
    acceptSupport,
    declineSupport,
    onFollowupDismissed,
  }
}

/** Attend que les écritures d'état en vol soient posées. Réservé aux tests. */
export function __flushPaywallDismissReasonWrites(): Promise<void> {
  return stateWrite
}

/** Remet l'état à zéro. Réservé aux tests : rien en production ne rouvre ça. */
export function __resetPaywallDismissReason() {
  isOpen.value = false
  followupOpen.value = false
  surface.value = ''
  paywallKind.value = 'native'
  chosenReason = null
  cancelLogged = true
  followupLogged = true
  followupChoice = null
  pendingAct = null
  askedThisSession = false
  stateWrite = Promise.resolve()
}
